import React, { useState, useEffect, lazy, Suspense } from 'react';
import { 
    getWebinars, createWebinar, updateWebinar, deleteWebinar,
    getCapsules, createCapsule, updateCapsule, deleteCapsule,
    getPapers, createPaper, updatePaper, deletePaper,
    getCourses, createCourse, updateCourse, deleteCourse, getCourseDetails,
    createModule, updateModule, deleteModule,
    createLesson, updateLesson, deleteLesson
} from '../../services/adminService';
import { 
    Plus, Edit, Trash2, Video, FileText, BookOpen, 
    Search, X, Save, ExternalLink, Clock, Tag, GraduationCap, ChevronDown, ChevronRight, ArrowLeft
} from 'lucide-react';
// Lazy-load ReactQuill to avoid issues during SSR/build and load its CSS on client
const ReactQuill = lazy(() => import('react-quill'));

const KnowledgeManager = () => {
    const [activeTab, setActiveTab] = useState('webinars');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});
    const [editorMode, setEditorMode] = useState('visual'); // 'visual', 'code', 'preview'

    // Course Content Management State
    const [managingCourse, setManagingCourse] = useState(null);
    const [courseDetails, setCourseDetails] = useState(null);
    const [expandedModules, setExpandedModules] = useState({});

    useEffect(() => {
        loadItems();
        // Load Quill CSS on client only
        if (typeof window !== 'undefined') {
            import('react-quill/dist/quill.snow.css').catch(() => {});
        }
    }, [activeTab]);

    const loadItems = async () => {
        setLoading(true);
        try {
            let data = [];
            if (activeTab === 'webinars') data = await getWebinars();
            else if (activeTab === 'capsules') data = await getCapsules();
            else if (activeTab === 'papers') data = await getPapers();
            else if (activeTab === 'courses') data = await getCourses();
            setItems(data);
        } catch (error) {
            console.error('Error loading items:', error);
            alert('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({ ...item });
        setEditorMode('visual');
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar este elemento?')) return;
        try {
            if (activeTab === 'webinars') await deleteWebinar(id);
            else if (activeTab === 'capsules') await deleteCapsule(id);
            else if (activeTab === 'papers') await deletePaper(id);
            else if (activeTab === 'courses') await deleteCourse(id);
            loadItems();
        } catch (error) {
            alert('Error al eliminar: ' + error.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        console.log('Submitting form with data:', formData);

        try {
            if (editingItem) {
                const id = editingItem.WebinarId || editingItem.CapsulaId || editingItem.PaperId || editingItem.CursoId;
                if (activeTab === 'webinars') await updateWebinar(id, formData);
                else if (activeTab === 'capsules') await updateCapsule(id, formData);
                else if (activeTab === 'papers') await updatePaper(id, formData);
                else if (activeTab === 'courses') await updateCourse(id, formData);
            } else {
                if (activeTab === 'webinars') await createWebinar(formData);
                else if (activeTab === 'capsules') await createCapsule(formData);
                else if (activeTab === 'papers') await createPaper(formData);
                else if (activeTab === 'courses') await createCourse(formData);
            }
            setIsModalOpen(false);
            loadItems();
        } catch (error) {
            alert('Error al guardar: ' + error.message);
        }
    };

    const openNewModal = () => {
        setEditingItem(null);
        setFormData({});
        setEditorMode('visual');
        setIsModalOpen(true);
    };

    // Module Modal State
    const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
    const [moduleFormData, setModuleFormData] = useState({ Modulo: '' });

    // --- Course Content Management ---

    const handleManageContent = async (course) => {
        setManagingCourse(course);
        setLoading(true);
        try {
            const details = await getCourseDetails(course.CursoId);
            setCourseDetails(details);
        } catch (error) {
            alert('Error al cargar detalles del curso: ' + error.message);
            setManagingCourse(null);
        } finally {
            setLoading(false);
        }
    };

    const openNewModuleModal = () => {
        setModuleFormData({ Modulo: '' });
        setIsModuleModalOpen(true);
    };

    const openEditModuleModal = (module) => {
        setModuleFormData({ 
            ModuloId: module.ModuloId, 
            Modulo: module.Modulo,
            Preguntas: module.Preguntas || [] 
        });
        setIsModuleModalOpen(true);
    };

    const handleModuleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (moduleFormData.ModuloId) {
                await updateModule(moduleFormData.ModuloId, moduleFormData);
            } else {
                await createModule({ 
                    CursoId: managingCourse.CursoId, 
                    Modulo: moduleFormData.Modulo,
                    Preguntas: moduleFormData.Preguntas 
                });
            }
            setIsModuleModalOpen(false);
            const details = await getCourseDetails(managingCourse.CursoId);
            setCourseDetails(details);
        } catch (error) {
            alert('Error al guardar módulo: ' + error.message);
        }
    };

    const handleDeleteModule = async (moduleId) => {
        if (!window.confirm('¿Eliminar módulo y todas sus lecciones?')) return;
        try {
            await deleteModule(moduleId);
            const details = await getCourseDetails(managingCourse.CursoId);
            setCourseDetails(details);
        } catch (error) {
            alert('Error al eliminar módulo: ' + error.message);
        }
    };

    const handleAddLesson = async (moduleId) => {
        // For simplicity, we'll use the main modal for lessons too, but we need to adapt it
        setEditingItem(null); // New item
        setFormData({ CursoId: managingCourse.CursoId, ModuloId: moduleId, DuracionMinutos: 10 });
        setEditorMode('visual');
        // We need a way to tell the modal we are creating a lesson, not a course/webinar
        // We can use a temporary "activeTab" override or a specific state
        // Let's use a specific state for "lessonModal" to avoid confusion
        setIsLessonModalOpen(true);
    };

    const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);

    // --- Question Management Helpers ---
    const addQuestion = () => {
        const newQuestions = [...(moduleFormData.Preguntas || [])];
        newQuestions.push({ Pregunta: '', Opciones: [] });
        setModuleFormData({ ...moduleFormData, Preguntas: newQuestions });
    };

    const removeQuestion = (index) => {
        const newQuestions = [...(moduleFormData.Preguntas || [])];
        newQuestions.splice(index, 1);
        setModuleFormData({ ...moduleFormData, Preguntas: newQuestions });
    };

    const updateQuestion = (index, value) => {
        const newQuestions = [...(moduleFormData.Preguntas || [])];
        newQuestions[index].Pregunta = value;
        setModuleFormData({ ...moduleFormData, Preguntas: newQuestions });
    };

    const addOption = (qIndex) => {
        const newQuestions = [...(moduleFormData.Preguntas || [])];
        if (!newQuestions[qIndex].Opciones) newQuestions[qIndex].Opciones = [];
        newQuestions[qIndex].Opciones.push({ Opcion: '', EsCorrecta: false });
        setModuleFormData({ ...moduleFormData, Preguntas: newQuestions });
    };

    const removeOption = (qIndex, oIndex) => {
        const newQuestions = [...(moduleFormData.Preguntas || [])];
        newQuestions[qIndex].Opciones.splice(oIndex, 1);
        setModuleFormData({ ...moduleFormData, Preguntas: newQuestions });
    };

    const updateOption = (qIndex, oIndex, field, value) => {
        const newQuestions = [...(moduleFormData.Preguntas || [])];
        newQuestions[qIndex].Opciones[oIndex][field] = value;
        setModuleFormData({ ...moduleFormData, Preguntas: newQuestions });
    };

    const handleLessonSubmit = async (e) => {
        e.preventDefault();
        try {
            if (formData.LeccionId) {
                await updateLesson(formData.LeccionId, formData);
            } else {
                await createLesson(formData);
            }
            setIsLessonModalOpen(false);
            const details = await getCourseDetails(managingCourse.CursoId);
            setCourseDetails(details);
        } catch (error) {
            alert('Error al guardar lección: ' + error.message);
        }
    };

    const handleEditLesson = (lesson) => {
        setFormData({ ...lesson });
        setEditorMode('visual');
        setIsLessonModalOpen(true);
    };

    const handleDeleteLesson = async (lessonId) => {
        if (!window.confirm('¿Eliminar lección?')) return;
        try {
            await deleteLesson(lessonId);
            const details = await getCourseDetails(managingCourse.CursoId);
            setCourseDetails(details);
        } catch (error) {
            alert('Error al eliminar lección: ' + error.message);
        }
    };

    const toggleModule = (moduleId) => {
        setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
    };

    const filteredItems = items.filter(item => 
        (item.Titulo || item.Curso || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const renderFormFields = () => {
        switch (activeTab) {
            case 'courses':
                return (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Nombre del Curso</label>
                            <input type="text" className="w-full px-4 py-2 rounded-lg border border-slate-200" 
                                value={formData.Curso || ''} onChange={e => setFormData({...formData, Curso: e.target.value})} required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                            <textarea className="w-full px-4 py-2 rounded-lg border border-slate-200" rows="3"
                                value={formData.Descripcion || ''} onChange={e => setFormData({...formData, Descripcion: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Nivel</label>
                            <select className="w-full px-4 py-2 rounded-lg border border-slate-200"
                                value={formData.Nivel || 'Principiante'} onChange={e => setFormData({...formData, Nivel: e.target.value})}>
                                <option value="Principiante">Principiante</option>
                                <option value="Intermedio">Intermedio</option>
                                <option value="Avanzado">Avanzado</option>
                            </select>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                            {/* Toggle Público */}
                            <div className={`flex flex-col gap-2 p-3 rounded-lg border ${formData.Publico ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200'}`}>
                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${formData.Publico ? 'text-blue-700' : 'text-slate-600'}`}>Público (Visible)</span>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, Publico: !prev.Publico }))}
                                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${formData.Publico ? 'bg-blue-600' : 'bg-slate-300'}`}
                                    >
                                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${formData.Publico ? 'translate-x-5' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                                <span className="text-xs text-slate-500">Visible para los usuarios en el catálogo</span>
                            </div>

                            {/* Toggle Activo */}
                            <div className={`flex flex-col gap-2 p-3 rounded-lg border ${formData.Activo ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}>
                                <div className="flex items-center justify-between">
                                    <span className={`text-sm font-medium ${formData.Activo ? 'text-green-700' : 'text-slate-600'}`}>Activo (En Producción)</span>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, Activo: !prev.Activo }))}
                                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${formData.Activo ? 'bg-green-600' : 'bg-slate-300'}`}
                                    >
                                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${formData.Activo ? 'translate-x-5' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                                <span className="text-xs text-slate-500">Disponible para ser asignado y cursado</span>
                            </div>
                        </div>
                    </>
                );
            case 'webinars':
                return (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Título</label>
                            <input type="text" className="w-full px-4 py-2 rounded-lg border border-slate-200" 
                                value={formData.Titulo || ''} onChange={e => setFormData({...formData, Titulo: e.target.value})} required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                            <textarea className="w-full px-4 py-2 rounded-lg border border-slate-200" rows="3"
                                value={formData.Descripcion || ''} onChange={e => setFormData({...formData, Descripcion: e.target.value})} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Fecha y Hora</label>
                                <input type="datetime-local" className="w-full px-4 py-2 rounded-lg border border-slate-200" 
                                    value={formData.FechaHora ? new Date(formData.FechaHora).toISOString().slice(0, 16) : ''} 
                                    onChange={e => setFormData({...formData, FechaHora: e.target.value})} required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Duración (min)</label>
                                <input type="number" className="w-full px-4 py-2 rounded-lg border border-slate-200" 
                                    value={formData.DuracionMinutos || 60} onChange={e => setFormData({...formData, DuracionMinutos: e.target.value})} />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Tags (separados por coma)</label>
                            <input type="text" className="w-full px-4 py-2 rounded-lg border border-slate-200" 
                                value={formData.Tags || ''} onChange={e => setFormData({...formData, Tags: e.target.value})} placeholder="Compliance, Normativa, ..." />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Link del Video/Reunión</label>
                            <input type="url" className="w-full px-4 py-2 rounded-lg border border-slate-200" 
                                value={formData.Link || ''} onChange={e => setFormData({...formData, Link: e.target.value})} />
                        </div>
                    </>
                );
            case 'capsules':
                return (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Título</label>
                            <input type="text" className="w-full px-4 py-2 rounded-lg border border-slate-200" 
                                value={formData.Titulo || ''} onChange={e => setFormData({...formData, Titulo: e.target.value})} required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Descripción Corta</label>
                            <textarea className="w-full px-4 py-2 rounded-lg border border-slate-200" rows="2"
                                value={formData.Descripcion || ''} onChange={e => setFormData({...formData, Descripcion: e.target.value})} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Categoría</label>
                                <input type="text" className="w-full px-4 py-2 rounded-lg border border-slate-200" 
                                    value={formData.Categoria || ''} onChange={e => setFormData({...formData, Categoria: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Duración Lectura (min)</label>
                                <input type="number" className="w-full px-4 py-2 rounded-lg border border-slate-200" 
                                    value={formData.DuracionMinutos || 5} onChange={e => setFormData({...formData, DuracionMinutos: e.target.value})} />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Contenido</label>
                            
                            <div className="flex gap-2 mb-2 border-b border-slate-200">
                                <button 
                                    type="button"
                                    onClick={() => setEditorMode('visual')}
                                    className={`px-3 py-2 text-sm font-medium ${editorMode === 'visual' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    Editor Visual
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => setEditorMode('code')}
                                    className={`px-3 py-2 text-sm font-medium ${editorMode === 'code' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    Código HTML
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => setEditorMode('preview')}
                                    className={`px-3 py-2 text-sm font-medium ${editorMode === 'preview' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    Vista Previa
                                </button>
                            </div>

                            <div className="h-80">
                                {editorMode === 'visual' && (
                                    <Suspense fallback={<div className="p-4">Cargando editor...</div>}>
                                        <ReactQuill 
                                            theme="snow"
                                            value={formData.Contenido || ''} 
                                            onChange={value => setFormData({...formData, Contenido: value})}
                                            className="h-64"
                                        />
                                    </Suspense>
                                )}
                                {editorMode === 'code' && (
                                    <textarea 
                                        className="w-full h-full p-4 font-mono text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                        value={formData.Contenido || ''}
                                        onChange={e => setFormData({...formData, Contenido: e.target.value})}
                                        placeholder="<p>Escriba su HTML aquí...</p>"
                                    />
                                )}
                                {editorMode === 'preview' && (
                                    <div className="w-full h-full p-4 border border-slate-200 rounded-lg overflow-y-auto prose prose-sm max-w-none bg-gray-50">
                                        <div dangerouslySetInnerHTML={{ __html: formData.Contenido || '<p class="text-slate-400 italic">Sin contenido...</p>' }} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                );
            case 'papers':
                return (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Título</label>
                            <input type="text" className="w-full px-4 py-2 rounded-lg border border-slate-200" 
                                value={formData.Titulo || ''} onChange={e => setFormData({...formData, Titulo: e.target.value})} required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                            <textarea className="w-full px-4 py-2 rounded-lg border border-slate-200" rows="3"
                                value={formData.Descripcion || ''} onChange={e => setFormData({...formData, Descripcion: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Link de Descarga (PDF)</label>
                            <input type="url" className="w-full px-4 py-2 rounded-lg border border-slate-200" 
                                value={formData.LinkDescarga || ''} onChange={e => setFormData({...formData, LinkDescarga: e.target.value})} />
                        </div>
                    </>
                );
            default: return null;
        }
    };

    if (managingCourse) {
        return (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="flex items-center gap-4 border-b border-slate-200 pb-4">
                    <button 
                        onClick={() => setManagingCourse(null)}
                        className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                    >
                        <ArrowLeft size={24} className="text-slate-600" />
                    </button>
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800">{managingCourse.Curso}</h2>
                        <p className="text-slate-500 text-sm">Gestión de Módulos y Lecciones</p>
                    </div>
                    <button 
                        onClick={openNewModuleModal}
                        className="ml-auto flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm font-medium"
                    >
                        <Plus size={20} /> Nuevo Módulo
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-12 text-slate-500">Cargando contenido...</div>
                ) : (
                    <div className="space-y-4">
                        {courseDetails?.Modulos?.length === 0 && (
                            <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-200 text-slate-400">
                                Este curso aún no tiene módulos. ¡Crea el primero!
                            </div>
                        )}
                        {courseDetails?.Modulos?.map((modulo) => (
                            <div key={modulo.ModuloId} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                <div 
                                    className="p-4 bg-slate-50 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
                                    onClick={() => toggleModule(modulo.ModuloId)}
                                >
                                    <div className="flex items-center gap-3">
                                        {expandedModules[modulo.ModuloId] ? <ChevronDown size={20} className="text-slate-400" /> : <ChevronRight size={20} className="text-slate-400" />}
                                        <h3 className="font-bold text-slate-700">{modulo.Modulo}</h3>
                                        <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded-full">
                                            {modulo.Lecciones?.length || 0} lecciones
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); openEditModuleModal(modulo); }}
                                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                            title="Editar nombre del módulo"
                                        >
                                            <Edit size={18} />
                                        </button>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleAddLesson(modulo.ModuloId); }}
                                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg text-sm font-medium flex items-center gap-1"
                                        >
                                            <Plus size={16} /> Lección
                                        </button>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleDeleteModule(modulo.ModuloId); }}
                                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                                
                                {expandedModules[modulo.ModuloId] && (
                                    <div className="divide-y divide-slate-100">
                                        {modulo.Lecciones?.length === 0 && (
                                            <div className="p-4 text-center text-sm text-slate-400 italic">
                                                No hay lecciones en este módulo.
                                            </div>
                                        )}
                                        {modulo.Lecciones?.map((leccion) => (
                                            <div key={leccion.LeccionId} className="p-4 pl-12 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                                                        <FileText size={16} />
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-slate-700">{leccion.Leccion}</div>
                                                        <div className="text-xs text-slate-400 flex items-center gap-2">
                                                            <Clock size={12} /> {leccion.DuracionMinutos} min
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex gap-1">
                                                    <button 
                                                        onClick={() => handleEditLesson(leccion)}
                                                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteLesson(leccion.LeccionId)}
                                                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Module Modal */}
                {isModuleModalOpen && (
                    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
                            <div className="p-6 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white z-10">
                                <h2 className="text-xl font-bold text-slate-800">
                                    {moduleFormData.ModuloId ? 'Editar Módulo' : 'Nuevo Módulo'}
                                </h2>
                                <button onClick={() => setIsModuleModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                    <X size={24} />
                                </button>
                            </div>
                            <form onSubmit={handleModuleSubmit} className="p-6 space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Nombre del Módulo</label>
                                    <input 
                                        type="text" 
                                        className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-blue-500 outline-none"
                                        value={moduleFormData.Modulo} 
                                        onChange={e => setModuleFormData({...moduleFormData, Modulo: e.target.value})} 
                                        required 
                                        autoFocus
                                    />
                                </div>

                                <div className="border-t border-slate-200 pt-6">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-bold text-slate-800">Evaluación del Módulo</h3>
                                        <button type="button" onClick={addQuestion} className="text-sm bg-indigo-50 text-indigo-600 px-3 py-1.5 rounded-lg font-medium hover:bg-indigo-100 transition-colors flex items-center gap-1">
                                            <Plus size={16} /> Agregar Pregunta
                                        </button>
                                    </div>
                                    
                                    <div className="space-y-6">
                                        {(!moduleFormData.Preguntas || moduleFormData.Preguntas.length === 0) && (
                                            <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200 text-slate-400 text-sm">
                                                No hay preguntas definidas para este módulo.
                                            </div>
                                        )}
                                        {moduleFormData.Preguntas?.map((pregunta, qIndex) => (
                                            <div key={qIndex} className="bg-slate-50 p-4 rounded-xl border border-slate-200 relative group">
                                                <button 
                                                    type="button" 
                                                    onClick={() => removeQuestion(qIndex)}
                                                    className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                                    title="Eliminar pregunta"
                                                >
                                                    <X size={16} />
                                                </button>
                                                
                                                <div className="mb-3 pr-8">
                                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Pregunta {qIndex + 1}</label>
                                                    <input 
                                                        type="text" 
                                                        className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 outline-none bg-white"
                                                        placeholder="Escribe la pregunta aquí..."
                                                        value={pregunta.Pregunta}
                                                        onChange={(e) => updateQuestion(qIndex, e.target.value)}
                                                        required
                                                    />
                                                </div>

                                                <div className="space-y-2 pl-4 border-l-2 border-slate-200">
                                                    {pregunta.Opciones?.map((opcion, oIndex) => (
                                                        <div key={oIndex} className="flex items-center gap-2">
                                                            <input 
                                                                type="radio" 
                                                                name={`correct-${qIndex}`}
                                                                checked={opcion.EsCorrecta}
                                                                onChange={(e) => {
                                                                    // Uncheck others
                                                                    const newQuestions = [...moduleFormData.Preguntas];
                                                                    newQuestions[qIndex].Opciones.forEach((o, i) => o.EsCorrecta = (i === oIndex));
                                                                    setModuleFormData({...moduleFormData, Preguntas: newQuestions});
                                                                }}
                                                                className="w-4 h-4 text-blue-600"
                                                                title="Marcar como respuesta correcta"
                                                            />
                                                            <input 
                                                                type="text" 
                                                                className={`flex-grow px-3 py-1.5 rounded-lg border focus:border-blue-500 outline-none text-sm ${opcion.EsCorrecta ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white'}`}
                                                                placeholder={`Opción ${oIndex + 1}`}
                                                                value={opcion.Opcion}
                                                                onChange={(e) => updateOption(qIndex, oIndex, 'Opcion', e.target.value)}
                                                                required
                                                            />
                                                            <button 
                                                                type="button" 
                                                                onClick={() => removeOption(qIndex, oIndex)}
                                                                className="text-slate-400 hover:text-red-500"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button 
                                                        type="button" 
                                                        onClick={() => addOption(qIndex)}
                                                        className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 mt-2"
                                                    >
                                                        <Plus size={12} /> Agregar Opción
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 pt-2">
                                    <button type="button" onClick={() => setIsModuleModalOpen(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium">Cancelar</button>
                                    <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
                                        Guardar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Lesson Modal */}
                {isLessonModalOpen && (
                    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
                            <div className="p-6 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white z-10">
                                <h2 className="text-xl font-bold text-slate-800">
                                    {formData.LeccionId ? 'Editar Lección' : 'Nueva Lección'}
                                </h2>
                                <button onClick={() => setIsLessonModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                    <X size={24} />
                                </button>
                            </div>
                            <form onSubmit={handleLessonSubmit} className="p-6 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Título de la Lección</label>
                                        <input type="text" className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" 
                                            value={formData.Leccion || ''} onChange={e => setFormData({...formData, Leccion: e.target.value})} required />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Duración (minutos)</label>
                                        <input type="number" className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" 
                                            value={formData.DuracionMinutos || 10} onChange={e => setFormData({...formData, DuracionMinutos: e.target.value})} />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">URL del Video (Opcional)</label>
                                    <input type="url" className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" 
                                        placeholder="https://vimeo.com/..."
                                        value={formData.VideoURL || ''} onChange={e => setFormData({...formData, VideoURL: e.target.value})} />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">Contenido de Valor (Texto de Comprensión)</label>
                                                            <div className="h-64 mb-12">
                                                                <Suspense fallback={<div className="p-4">Cargando editor...</div>}>
                                                                    <ReactQuill 
                                                                        theme="snow"
                                                                        value={formData.Contenido || ''} 
                                                                        onChange={value => setFormData({...formData, Contenido: value})}
                                                                        className="h-56"
                                                                    />
                                                                </Suspense>
                                                            </div>
                                </div>

                                <div className="border-t border-slate-200 pt-6">
                                    <div className="flex justify-end gap-3 pt-4">
                                        <button type="button" onClick={() => setIsLessonModalOpen(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium">Cancelar</button>
                                        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2">
                                            Guardar
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-2xl font-bold text-slate-800">Base de Conocimiento</h2>
                <button onClick={openNewModal} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium">
                    <Plus size={20} /> {
                        activeTab === 'webinars' ? 'Nuevo Webinar' : 
                        activeTab === 'capsules' ? 'Nueva Cápsula' : 
                        activeTab === 'papers' ? 'Nuevo Paper' : 'Nuevo Curso'
                    }
                </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
                {[
                    { id: 'webinars', label: 'Webinars', icon: Video },
                    { id: 'capsules', label: 'Cápsulas', icon: BookOpen },
                    { id: 'papers', label: 'Papers', icon: FileText },
                    { id: 'courses', label: 'Cursos', icon: GraduationCap }
                ].map(tab => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors whitespace-nowrap ${
                                activeTab === tab.id 
                                ? 'border-blue-600 text-blue-600' 
                                : 'border-transparent text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            <Icon size={18} /> {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <input 
                    type="text" 
                    placeholder="Buscar..." 
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 outline-none"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Grid */}
            {loading ? (
                <div className="text-center py-12 text-slate-500">Cargando...</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredItems.map((item) => (
                        <div key={item.WebinarId || item.CapsulaId || item.PaperId || item.CursoId} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex flex-col">
                            <div className="flex justify-between items-start mb-4">
                                <div className={`p-3 rounded-lg ${
                                    activeTab === 'webinars' ? 'bg-blue-50 text-blue-600' :
                                    activeTab === 'capsules' ? 'bg-amber-50 text-amber-600' :
                                    activeTab === 'papers' ? 'bg-emerald-50 text-emerald-600' :
                                    'bg-indigo-50 text-indigo-600'
                                }`}>
                                    {activeTab === 'webinars' ? <Video size={24} /> :
                                     activeTab === 'capsules' ? <BookOpen size={24} /> :
                                     activeTab === 'papers' ? <FileText size={24} /> :
                                     <GraduationCap size={24} />}
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => handleEdit(item)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                                        <Edit size={18} />
                                    </button>
                                    <button onClick={() => handleDelete(item.WebinarId || item.CapsulaId || item.PaperId || item.CursoId)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                            
                            <h3 className="text-lg font-bold text-slate-800 mb-2 line-clamp-2">{item.Titulo || item.Curso}</h3>
                            <p className="text-sm text-slate-500 mb-4 line-clamp-3 flex-grow">{item.Descripcion}</p>
                            
                            <div className="mt-auto pt-4 border-t border-slate-100 text-xs text-slate-400 flex flex-col gap-1">
                                {activeTab === 'webinars' && (
                                    <>
                                        <div className="flex items-center gap-2"><Clock size={12}/> {new Date(item.FechaHora).toLocaleDateString()} - {item.DuracionMinutos} min</div>
                                        <div className="flex items-center gap-2"><Tag size={12}/> {item.Tags}</div>
                                    </>
                                )}
                                {activeTab === 'capsules' && (
                                    <div className="flex items-center gap-2"><Clock size={12}/> {item.DuracionMinutos} min lectura - {item.Categoria}</div>
                                )}
                                {activeTab === 'papers' && item.LinkDescarga && (
                                    <div className="flex items-center gap-2 text-blue-500"><ExternalLink size={12}/> Link disponible</div>
                                )}
                                {activeTab === 'courses' && (
                                    <div className="flex flex-col gap-2 w-full">
                                        <div className="flex justify-between items-center">
                                            <span className="bg-slate-100 px-2 py-1 rounded text-slate-600">{item.Nivel}</span>
                                            <div className="flex gap-2">
                                                {item.Publico ? <span className="text-blue-600 font-medium">Público</span> : <span className="text-slate-400">Privado</span>}
                                                {item.Activo ? <span className="text-green-600 font-medium">Activo</span> : <span className="text-slate-400">Inactivo</span>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 text-slate-500">
                                            <Clock size={14} /> <span>{item.TotalMinutos || 0} min totales</span>
                                        </div>
                                        <button 
                                            onClick={() => handleManageContent(item)}
                                            className="w-full mt-2 py-2 bg-indigo-50 text-indigo-600 rounded-lg font-medium hover:bg-indigo-100 transition-colors text-center"
                                        >
                                            Gestionar Contenido
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white z-10">
                            <h2 className="text-xl font-bold text-slate-800">
                                {editingItem ? 'Editar' : 'Nuevo'} {activeTab === 'webinars' ? 'Webinar' : activeTab === 'capsules' ? 'Cápsula' : activeTab === 'papers' ? 'Paper' : 'Curso'}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            {renderFormFields()}
                            <div className="flex justify-end gap-3 pt-4">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium">Cancelar</button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2">
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KnowledgeManager;